import clsx from "clsx";
import _ from "lodash";
import moment from "moment";
import React, { forwardRef, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import "react-datepicker/dist/react-datepicker.css";
import { MasterSelect } from "../common/MasterSelect";

import { CheckBox, FormGrid, FormGroup, FormRow, Spacer } from "@/components/common";
import { FilterConfigGroup } from "@/components/common/SearchCondition";
import { SearchConditionConfigModal } from "@/components/common/SearchCondition/SearchConditionConfigModal";
import { SearchConditionDisplayButton } from "@/components/common/SearchCondition/SearchConditionDisplayButton";
import { SettingIconButton } from "@/components/common/SettingIconButton";
import { useSearchConditionConfig } from "@/hooks/useSearchConditionConfig";
import { ApprovalSearchKey, ApprovalSearchKeyType, ApprovalSearchParams } from "@/models/approval";
import { FilterConfig } from "@/models/filterConfig";
import { ConstructionMasters } from "@/models/masters";
import { SearchConditionConfigDefinition, ScreenName } from "@/models/screenConfig";

import format from "date-fns/format";

type StateToProps = {
  masters: ConstructionMasters;
  areaIds: number[];
  deviceIds: number[];
  categoryId: number;
  companyIds: number[];
  facilityManagementIds: number[];
  constructionManagementIds: number[];
  primaryChargeIds: number[];
  processMajorClassIds: number[];
  processMiddleClassIds: number[];
  checkpointIds: number[];
  scheduleEndDate: string;
  itemText: string;
  processText: string;
  otherIds: number[];
  filter: number[];
};

type DispatchToProps = {
  changeDevice: (deviceIds: number[]) => void;
  changeArea: (areaIds: number[]) => void;
  changeCategory: (categoryId: number) => void;
  changeFacilityManagement: (facilityManagementIds: number[]) => void;
  changeConstructionManagement: (constructionManagementIds: number[]) => void;
  changePrimaryCharge: (primaryChargeIds: number[]) => void;
  changeOther: (otherIds: number[]) => void;
  changeProcessMajorClass: (processMajorClassIds: number[]) => void;
  changeCheckpoint: (checkpointIds: number[]) => void;
  changeCompany: (companyIds: number[]) => void;
  changeProcessMiddleClass: (processMiddleClassIds: number[]) => void;
  changeScheduleEndDate: (scheduleEndDate: string) => void;
  changeItemText: (itemText: string) => void;
  changeProcessText: (processText: string) => void;
  changeFilter: (filter: number) => void;
  changeFilters: (filters: number[]) => void;
  handleClear: () => void;
  handleSearch: (option?: { params?: ApprovalSearchParams }) => void;
  resetSearch: () => void;
};

type Props = StateToProps & DispatchToProps;

const ApprovalSearchPanel = forwardRef<HTMLDivElement, Props>(
  (
    {
      masters,
      areaIds,
      deviceIds,
      categoryId,
      companyIds,
      facilityManagementIds,
      constructionManagementIds,
      primaryChargeIds,
      otherIds,
      filter,
      processMajorClassIds,
      processMiddleClassIds,
      scheduleEndDate,
      checkpointIds,
      itemText,
      processText,
      changeArea,
      changeDevice,
      changeCategory,
      changeFacilityManagement,
      changeConstructionManagement,
      changePrimaryCharge,
      changeOther,
      changeProcessMajorClass,
      changeScheduleEndDate,
      changeCheckpoint,
      changeCompany,
      changeProcessMiddleClass,
      changeItemText,
      changeProcessText,
      changeFilter,
      changeFilters,
      handleClear,
      resetSearch,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    // データ取り込み画面の検索条件設定の定義
    const approvalSearchConditionDefinitions = useMemo<SearchConditionConfigDefinition<ApprovalSearchKeyType>[]>(
      () => [
        {
          itemsPerLine: 3,
          items: [
            { key: ApprovalSearchKey.Area, label: t("area"), required: false },
            { key: ApprovalSearchKey.Device, label: t("device"), required: false },
            { key: ApprovalSearchKey.FacilityManagement, label: t("facility_management"), required: false },
            { key: ApprovalSearchKey.Category, label: t("machines_category"), required: false },
            { key: ApprovalSearchKey.ConstructionManagement, label: t("construction_management"), required: false },
            { key: ApprovalSearchKey.PrimaryCharge, label: t("primary_charge"), required: false },
            { key: ApprovalSearchKey.Other, label: t("other"), required: false },
            { key: ApprovalSearchKey.ProcessMajorClass, label: t("process_major_class"), required: false },
            { key: ApprovalSearchKey.Company, label: t("company"), required: false },
            { key: ApprovalSearchKey.ProcessMiddleClass, label: t("process_middle_class"), required: false },
            { key: ApprovalSearchKey.ScheduleEndDate, label: t("scheduled_date"), required: false },
            { key: ApprovalSearchKey.Checkpoint, label: t("approval_confirm"), required: false },
            { key: ApprovalSearchKey.ItemText, label: t("title"), required: false },
            { key: ApprovalSearchKey.ProcessText, label: t("process_title"), required: false },
            { key: ApprovalSearchKey.NotStarted, label: t("not_started"), required: false },
            { key: ApprovalSearchKey.Coming, label: t("coming"), required: false },
            { key: ApprovalSearchKey.Expired, label: t("expired"), required: false },
            { key: ApprovalSearchKey.Completed, label: t("completed"), required: false },
            { key: ApprovalSearchKey.PreworkCompletionReport, label: t("prework_completion_report"), required: false },
          ],
        },
      ],
      [t]
    );

    // 検索条件設定
    const config = useSearchConditionConfig<ApprovalSearchKeyType>({
      screenName: ScreenName.APPROVAL,
      requiredKeys: approvalSearchConditionDefinitions.flatMap((v) =>
        v.items.filter((item) => item.required).map((item) => item.key)
      ),
    });
    const filterSearchConditionByConfig = (condition?: ApprovalSearchParams): ApprovalSearchParams => {
      const params = condition || {
        areaIds,
        deviceIds,
        facilityManagementIds,
        categoryId,
        constructionManagementIds,
        primaryChargeIds,
        otherIds,
        processMajorClassIds,
        companyIds,
        processMiddleClassIds,
        scheduleEndDate,
        checkpointIds,
        itemText,
        processText,
        filter,
      };
      if (!config.map || config.showAllConditions) return params;

      return {
        areaIds: config.map.get(ApprovalSearchKey.Area) ? params.areaIds : [],
        deviceIds: config.map.get(ApprovalSearchKey.Device) ? params.deviceIds : [],
        facilityManagementIds: config.map.get(ApprovalSearchKey.FacilityManagement) ? params.facilityManagementIds : [],
        categoryId: config.map.get(ApprovalSearchKey.Category) ? params.categoryId : 0,
        constructionManagementIds: config.map.get(ApprovalSearchKey.ConstructionManagement)
          ? params.constructionManagementIds
          : [],
        primaryChargeIds: config.map.get(ApprovalSearchKey.PrimaryCharge) ? params.primaryChargeIds : [],
        otherIds: config.map.get(ApprovalSearchKey.Other) ? params.otherIds : [],
        processMajorClassIds: config.map.get(ApprovalSearchKey.ProcessMajorClass) ? params.processMajorClassIds : [],
        companyIds: config.map.get(ApprovalSearchKey.Company) ? params.companyIds : [],
        processMiddleClassIds: config.map.get(ApprovalSearchKey.ProcessMiddleClass) ? params.processMiddleClassIds : [],
        scheduleEndDate: config.map.get(ApprovalSearchKey.ScheduleEndDate) ? params.scheduleEndDate : "",
        checkpointIds: config.map.get(ApprovalSearchKey.Checkpoint) ? params.checkpointIds : [],
        itemText: config.map.get(ApprovalSearchKey.ItemText) ? params.itemText : "",
        processText: config.map.get(ApprovalSearchKey.ProcessText) ? params.processText : "",
        filter: [
          config.map.get(ApprovalSearchKey.NotStarted) && (params.filter ?? []).includes(1) ? 1 : undefined,
          config.map.get(ApprovalSearchKey.Coming) && (params.filter ?? []).includes(2) ? 2 : undefined,
          config.map.get(ApprovalSearchKey.Expired) && (params.filter ?? []).includes(3) ? 3 : undefined,
          config.map.get(ApprovalSearchKey.Completed) && (params.filter ?? []).includes(4) ? 4 : undefined,
          config.map.get(ApprovalSearchKey.PreworkCompletionReport) && (params.filter ?? []).includes(5)
            ? 5
            : undefined,
        ].filter((v) => !!v),
      };
    };

    // 検索条件保存
    const getCurrentConditionLabels = () => {
      // 現在の条件を保存モーダルに表示するために編集する
      const p = filterSearchConditionByConfig();
      const formatDate = (d: string | null): string => (d ? format(new Date(d), "yyyy/MM/dd") : "");
      const names: { label: string; values: string[] | null }[] = [
        {
          label: t("area"),
          values: p.areaIds?.map((id) => masters?.areas.find((v) => v.area_id === id)?.area_name),
        },
        {
          label: t("device"),
          values: p.deviceIds?.map((id) => masters?.devices.find((v) => v.device_id === id)?.device_name),
        },
        {
          label: t("facility_management"),
          values: p.facilityManagementIds?.map(
            (id) => masters?.facility_managements.find((v) => v.facility_management_id === id)?.facility_management_name
          ),
        },
        {
          label: t("machines_category"),
          values: [masters?.categories.find((v) => v.category_id === p.categoryId)?.category_name],
        },
        {
          label: t("construction_management"),
          values: p.constructionManagementIds?.map(
            (id) =>
              masters?.construction_managements.find((v) => v.construction_management_id === id)
                ?.construction_management_name
          ),
        },
        {
          label: t("primary_charge"),
          values: p.primaryChargeIds?.map(
            (id) => masters?.primary_charges.find((v) => v.primary_charge_id === id)?.primary_charge_name
          ),
        },
        {
          label: t("other"),
          values: p.otherIds?.map((id) => masters?.others.find((v) => v.other_id === id)?.other_name),
        },
        {
          label: t("process_major_class"),
          values: p.processMajorClassIds?.map(
            (id) =>
              masters?.process_major_classes.find((v) => v.process_major_class_id === id)?.process_major_class_name
          ),
        },
        {
          label: t("company"),
          values: p.companyIds?.map((id) => masters?.companies.find((v) => v.company_id === id)?.company_name),
        },
        {
          label: t("process_middle_class"),
          values: p.processMiddleClassIds?.map(
            (id) =>
              masters?.process_middle_classes.find((v) => v.process_middle_class_id === id)?.process_middle_class_name
          ),
        },
        {
          label: t("scheduled_date"),
          values: [p.scheduleEndDate ? formatDate(p.scheduleEndDate) : ""],
        },
        {
          label: t("approval_confirm"),
          values: p.checkpointIds?.map(
            (id) => masters?.checkpoints.find((v) => v.checkpoint_id === id)?.checkpoint_name
          ),
        },
        {
          label: t("title"),
          values: [p.itemText],
        },
        {
          label: t("process_title"),
          values: [p.processText],
        },
        {
          label: t("check"),
          values: [
            p.filter?.includes(1) ? t("not_started") : "",
            p.filter?.includes(2) ? t("coming") : "",
            p.filter?.includes(3) ? t("expired") : "",
            p.filter?.includes(4) ? t("completed") : "",
            p.filter?.includes(5) ? t("prework_completion_report") : "",
          ],
        },
      ];

      return names.map((n) => ({ label: n.label, values: _.compact(n.values) })).filter((n) => n.values?.length);
    };
    const currentSearchParams = useMemo<ApprovalSearchParams>(() => {
      return filterSearchConditionByConfig();
    }, [
      areaIds,
      deviceIds,
      facilityManagementIds,
      categoryId,
      constructionManagementIds,
      primaryChargeIds,
      otherIds,
      processMajorClassIds,
      companyIds,
      processMiddleClassIds,
      scheduleEndDate,
      checkpointIds,
      itemText,
      processText,
      filter,
      config.map,
      config.showAllConditions,
    ]);
    const handleApplyFilterConfig = (filter: FilterConfig<ApprovalSearchParams>) => {
      // 検索条件設定で表示している検索条件のみ復元する
      const condition = filterSearchConditionByConfig(filter.config);

      changeArea(condition.areaIds ?? []);
      changeDevice(condition.deviceIds ?? []);
      changeFacilityManagement(condition.facilityManagementIds ?? []);
      changeCategory(condition.categoryId ?? 0);
      changeConstructionManagement(condition.constructionManagementIds ?? []);
      changePrimaryCharge(condition.primaryChargeIds ?? []);
      changeOther(condition.otherIds ?? []);
      changeProcessMajorClass(condition.processMajorClassIds ?? []);
      changeCompany(condition.companyIds ?? []);
      changeProcessMiddleClass(condition.processMiddleClassIds ?? []);
      changeScheduleEndDate(condition.scheduleEndDate ?? "");
      changeCheckpoint(condition.checkpointIds ?? []);
      changeItemText(condition.itemText ?? "");
      changeProcessText(condition.processText ?? "");
      changeFilters(condition.filter ?? []);
    };

    const handleSearch = (e: React.MouseEvent) => {
      e.preventDefault();

      if (config.showAllConditions) {
        props.handleSearch();
      } else {
        // 検索条件設定で表示している検索条件のみ有効とする
        props.handleSearch({ params: filterSearchConditionByConfig() });
      }
    };

    const display = useCallback(
      (target: ApprovalSearchKeyType): string => {
        return config.show([target]) ? "" : "hidden";
      },
      [config.show]
    );

    if (!config.map) return <></>;

    return (
      <div ref={ref}>
        <div className="flex justify-between bg-[#e4e4e4] w-[1200px]">
          <FilterConfigGroup
            screenName={ScreenName.APPROVAL}
            getCurrentConditionLabels={getCurrentConditionLabels}
            currentConfig={currentSearchParams}
            applyFilterConfig={handleApplyFilterConfig}
          />
          <SettingIconButton className="w-[32px] p-[8px]" onClick={config.openSettingModal} />
        </div>
        <div className="search-box">
          <div className="grid gap-y-[16px]">
            <FormGrid
              className="grid-cols-4"
              display={config.show([
                ApprovalSearchKey.Area,
                ApprovalSearchKey.Device,
                ApprovalSearchKey.FacilityManagement,
                ApprovalSearchKey.Category,
                ApprovalSearchKey.ConstructionManagement,
                ApprovalSearchKey.PrimaryCharge,
                ApprovalSearchKey.Other,
                ApprovalSearchKey.ProcessMajorClass,
                ApprovalSearchKey.Company,
                ApprovalSearchKey.ProcessMiddleClass,
              ])}
            >
              <FormGroup title={t("area")} display={config.show([ApprovalSearchKey.Area])}>
                <MasterSelect
                  prefix="area"
                  isMulti={true}
                  options={masters?.areas || []}
                  value={areaIds}
                  onChange={changeArea}
                />
              </FormGroup>
              <FormGroup title={t("device")} display={config.show([ApprovalSearchKey.Device])}>
                <MasterSelect
                  prefix="device"
                  isMulti={true}
                  options={masters?.devices || []}
                  value={deviceIds}
                  onChange={changeDevice}
                />
              </FormGroup>
              <FormGroup title={t("facility_management")} display={config.show([ApprovalSearchKey.FacilityManagement])}>
                <MasterSelect
                  prefix="facility_management"
                  isMulti={true}
                  options={masters?.facility_managements || []}
                  value={facilityManagementIds}
                  onChange={changeFacilityManagement}
                />
              </FormGroup>
              <FormGroup title={t("machines_category")} display={config.show([ApprovalSearchKey.Category])}>
                <MasterSelect
                  prefix="category"
                  isMulti={false}
                  options={masters?.categories || []}
                  value={categoryId}
                  onChange={changeCategory}
                />
              </FormGroup>
              <FormGroup
                title={t("construction_management")}
                display={config.show([ApprovalSearchKey.ConstructionManagement])}
              >
                <MasterSelect
                  prefix="construction_management"
                  isMulti={true}
                  options={masters?.construction_managements || []}
                  value={constructionManagementIds}
                  onChange={changeConstructionManagement}
                />
              </FormGroup>
              <FormGroup title={t("primary_charge")} display={config.show([ApprovalSearchKey.PrimaryCharge])}>
                <MasterSelect
                  prefix="primary_charge"
                  isMulti={true}
                  options={masters?.primary_charges || []}
                  value={primaryChargeIds}
                  onChange={changePrimaryCharge}
                />
              </FormGroup>
              <FormGroup title={t("other")} display={config.show([ApprovalSearchKey.Other])}>
                <MasterSelect
                  prefix="other"
                  isMulti={true}
                  options={masters?.others || []}
                  value={otherIds}
                  onChange={changeOther}
                />
              </FormGroup>
              <FormGroup title={t("process_major_class")} display={config.show([ApprovalSearchKey.ProcessMajorClass])}>
                <MasterSelect
                  prefix="process_major_class"
                  isMulti={true}
                  options={masters?.process_major_classes || []}
                  value={processMajorClassIds}
                  onChange={changeProcessMajorClass}
                />
              </FormGroup>
              <FormGroup title={t("company")} display={config.show([ApprovalSearchKey.Company])}>
                <MasterSelect
                  prefix="company"
                  options={masters?.companies || []}
                  value={companyIds}
                  onChange={changeCompany}
                  isMulti={true}
                />
              </FormGroup>
              <FormGroup
                title={t("process_middle_class")}
                display={config.show([ApprovalSearchKey.ProcessMiddleClass])}
              >
                <MasterSelect
                  prefix="process_middle_class"
                  options={masters?.process_middle_classes || []}
                  value={processMiddleClassIds}
                  onChange={changeProcessMiddleClass}
                  isMulti={true}
                />
              </FormGroup>
            </FormGrid>

            <FormGrid
              className="grid-cols-4"
              display={config.show([
                ApprovalSearchKey.ScheduleEndDate,
                ApprovalSearchKey.Checkpoint,
                ApprovalSearchKey.ItemText,
                ApprovalSearchKey.ProcessText,
              ])}
            >
              <FormGroup title={t("scheduled_date")} display={config.show([ApprovalSearchKey.ScheduleEndDate])}>
                <DatePicker
                  wrapperClassName="w-200"
                  selected={scheduleEndDate === "" ? null : moment(scheduleEndDate, "YYYY/MM/DD").toDate()}
                  dateFormat="yyyy/MM/dd"
                  locale={t("calender_locale")}
                  onChange={(date) =>
                    changeScheduleEndDate(date === null ? "" : moment(date).format("YYYY/MM/DD").toString())
                  }
                />
              </FormGroup>
              <FormGroup title={t("approval_confirm")} display={config.show([ApprovalSearchKey.Checkpoint])}>
                <MasterSelect
                  prefix="checkpoint"
                  isMulti={true}
                  options={masters?.checkpoints || []}
                  value={checkpointIds}
                  onChange={changeCheckpoint}
                />
              </FormGroup>
              <FormGroup title={t("title")} display={config.show([ApprovalSearchKey.ItemText])}>
                <input
                  data-test-id="text-approval-item-name"
                  type="text"
                  className="form-control w-200"
                  value={itemText}
                  onChange={(e) => changeItemText(e.target.value)}
                />
              </FormGroup>
              <FormGroup title={t("process_title")} display={config.show([ApprovalSearchKey.ProcessText])}>
                <input
                  data-test-id="text-approval-process-name"
                  type="text"
                  className="form-control w-200"
                  value={processText}
                  onChange={(e) => changeProcessText(e.target.value)}
                />
              </FormGroup>
            </FormGrid>
            <FormRow
              className="gap-x-[15px] ml-90"
              display={config.show([
                ApprovalSearchKey.NotStarted,
                ApprovalSearchKey.Coming,
                ApprovalSearchKey.Expired,
                ApprovalSearchKey.Completed,
                ApprovalSearchKey.PreworkCompletionReport,
              ])}
            >
              <div className={clsx("search-box__form left", display(ApprovalSearchKey.NotStarted))}>
                <CheckBox label={t("not_started")} checked={_.includes(filter, 1)} onChange={() => changeFilter(1)} />
              </div>
              <div className={clsx("search-box__form left", display(ApprovalSearchKey.Coming))}>
                <CheckBox label={t("coming")} checked={_.includes(filter, 2)} onChange={() => changeFilter(2)} />
              </div>
              <div className={clsx("search-box__form left", display(ApprovalSearchKey.Expired))}>
                <CheckBox label={t("expired")} checked={_.includes(filter, 3)} onChange={() => changeFilter(3)} />
              </div>
              <div className={clsx("search-box__form left", display(ApprovalSearchKey.Completed))}>
                <CheckBox label={t("completed")} checked={_.includes(filter, 4)} onChange={() => changeFilter(4)} />
              </div>
              <div className={clsx("search-box__form left", display(ApprovalSearchKey.PreworkCompletionReport))}>
                <CheckBox
                  label={t("prework_completion_report")}
                  checked={_.includes(filter, 5)}
                  onChange={() => changeFilter(5)}
                />
              </div>
            </FormRow>
            <FormRow className="justify-between mt-[16px]">
              <Spacer x="210px" />
              <SearchConditionDisplayButton
                isExpanded={config.showAllConditions}
                onChange={config.toggleDisplayOptionalConditions}
              />
              <div className="flex gap-x-4">
                <button
                  className="btn btn-gray"
                  onClick={() => {
                    resetSearch();
                    handleClear();
                  }}
                >
                  {t("reset")}
                </button>
                <button className="btn btn-blue" onClick={handleSearch}>
                  {t("search")}
                </button>
              </div>
            </FormRow>
          </div>
        </div>
        {config.showSettingModal && (
          <SearchConditionConfigModal<ApprovalSearchKeyType>
            title={t("search_condition_config")}
            configMap={config.map}
            definitions={approvalSearchConditionDefinitions}
            onClose={config.closeSettingModal}
            onSubmit={(c) => config.saveSearchItemConfig(c)}
          />
        )}
      </div>
    );
  }
);

ApprovalSearchPanel.displayName = "ApprovalSearchPanel";

export default ApprovalSearchPanel;
